import { useState, useRef } from "react"
import { IProductOrderDetail } from "interfaces/IProductOrderDetail"
import useUserPermissions from "hooks/UseUserPermissions"
import { ResourcePermissionsEnum } from "enums/ResourcePermissionsEnum"
// import { useGetProductInfoByClient } from "hooks/UseGetMultiClient"
import { ButtonStyled } from "theme/common/Button.styled"
import Modal from "components/Commons/Modal/Modal"
import styled from "styled-components"
import productPlaceholder from "assets/images/shiperto/product-image-default.svg"

/* ─── Card ──────────────────────────────────────────────────────────────── */
const Card = styled.div<{ $status?: "pending" | "approved" | "rejected" }>`
  position: relative;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 12px rgba(0, 0, 0, 0.1);
  padding: 0.5rem 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  min-height: 140px;
  border-left: 5px solid
    ${({ $status }) => ($status === "approved" ? "#2e7d32" : $status === "rejected" ? "#d32f2f" : "#e0e0e0")};
  transition: all 0.3s ease;
`

const ProductName = styled.p`
  margin: 0 0 0.4rem 0;
  font-weight: 700;
  font-size: 1.25rem;
  color: #1a1a1a;
  line-height: 1.3;
  padding-right: 2rem; /* space for reject btn */
`

/* ─── Body (image + info side by side) ─────────────────────────────────── */
const ProductBody = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;
`

const ProductImage = styled.div`
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  border: 1px solid #ebebeb;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ProductInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-top: 0.15rem;

  .info-row {
    display: flex;
    gap: 0.3rem;
    font-size: 1.1rem;
    color: #444;

    .label {
      font-weight: 600;
      color: #222;
    }
  }
`

/* ─── Status badge ──────────────────────────────────────────────────────── */
const StatusBadge = styled.span<{ $variant: "approved" | "rejected" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.4rem;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
  background: ${({ $variant }) => ($variant === "approved" ? "#e8f5e9" : "#fdecea")};
  color: ${({ $variant }) => ($variant === "approved" ? "#2e7d32" : "#c62828")};
`

/* ─── Reject button ─────────────────────────────────────────────────────── */
const RejectIconBtn = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 32px;
  height: 32px;
  border-radius: 50%;

  background: transparent;
  color: #888;
  font-size: 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.2s,
    color 0.2s,
    background 0.2s;
  line-height: 1;

  &:hover {
    border-color: #f44336;
    color: #f44336;
    background: #fdecea;
  }
`

/* ─── Modal content ─────────────────────────────────────────────────────── */
const RejectModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background-color: ${({ theme }) => theme.palette.white};
  padding: 2rem;
  border-radius: 8px;

  h3 {
    margin: 0;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.palette.grey80};
    font-size: 0.95rem;
    line-height: 1.5;
  }
  input {
    padding: 0.8rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 100%;
    box-sizing: border-box;
    font-size: 1rem;
  }
  input:focus {
    outline: none;
    border-color: #1976d2;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.15);
  }
  .error-msg {
    color: #d32f2f;
    font-size: 0.88rem;
    font-weight: 500;
  }
  .actions {
    display: flex;
    gap: 1rem;
    margin-top: 0.25rem;
    button {
      flex: 1;
      justify-content: center;
    }
  }
`

const DestructiveBtn = styled(ButtonStyled)`
  background-color: #d32f2f !important;
  border-color: #d32f2f !important;
  color: #fff !important;
  &:hover {
    background-color: #b71c1c !important;
    border-color: #b71c1c !important;
  }
`

/* ─── Types ─────────────────────────────────────────────────────────────── */
interface ProductItemRowProps extends IProductOrderDetail {
  pickingTransition: any
  dispachPickingTransition: any
  allOrderItems: any
}

/* ─── Component ─────────────────────────────────────────────────────────── */
const ProductItemRow = ({
  itemOrder,
  onClicProcessProduct,
  source,
  hideConfirmActions,
  pickingTransition,
  dispachPickingTransition,
  allOrderItems,
}: ProductItemRowProps) => {
  const { permission } = useUserPermissions({ resources: [ResourcePermissionsEnum.rejecProduct] })

  const [rejectSku, setRejectSku] = useState("")
  const [rejectError, setRejectError] = useState("")
  const modalRejectRef = useRef<any>(null)

  const openRejectModal = () => {
    setRejectSku("")
    setRejectError("")
    modalRejectRef.current?.open()
  }

  const handleConfirmReject = () => {
    if (rejectSku.trim() !== itemOrder?.sku) {
      setRejectError("El SKU ingresado no coincide con el producto.")
      return
    }
    modalRejectRef.current?.close()
    if (itemOrder) {
      onClicProcessProduct({ ...itemOrder, quantity: 1, isConfirm: false })
    }
  }

  // Derived state
  const isRejected = false
  const isApproved = false

  const cardStatus = "pending"
  const thumbnail = itemOrder?.custom?.thumbnail || productPlaceholder

  return (
    <>
      <Card $status={cardStatus}>
        {/* Reject button — only when not yet processed */}
        {permission[ResourcePermissionsEnum.rejecProduct] && !hideConfirmActions && (
          <RejectIconBtn onClick={openRejectModal} title="Rechazar producto" type="button">
            ⊘
          </RejectIconBtn>
        )}

        {/* Product name */}
        <ProductName>{itemOrder?.name}</ProductName>

        {/* Body: image + info */}
        <ProductBody>
          <ProductImage>
            <img
              src={thumbnail}
              alt={itemOrder?.name ?? "Producto"}
              onError={(e) => {
                ;(e.currentTarget as HTMLImageElement).src = productPlaceholder
              }}
            />
          </ProductImage>

          <ProductInfo>
            <div className="info-row">
              <span className="label">SKU:</span>
              <span>{itemOrder?.sku}</span>
            </div>
            <div className="info-row">
              <span className="label">Cantidad:</span>
              <span>{itemOrder?.quantity ?? 1}</span>
            </div>

            {/* Status badge */}
            {isApproved && <StatusBadge $variant="approved">✓ Aprobado</StatusBadge>}
            {isRejected && <StatusBadge $variant="rejected">✕ Rechazado</StatusBadge>}
          </ProductInfo>
        </ProductBody>
      </Card>

      {/* Reject confirmation modal */}
      <Modal ref={modalRejectRef}>
        <RejectModalContent>
          <h3>¿Está seguro que desea rechazar este producto?</h3>
          <p>
            Para confirmar el rechazo de <strong>{itemOrder?.name}</strong>, ingrese el SKU del producto:
          </p>
          <div>
            <input
              type="text"
              placeholder="Ingrese el SKU para validar"
              value={rejectSku}
              onChange={(e) => {
                setRejectSku(e.target.value)
                setRejectError("")
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleConfirmReject()
              }}
              autoFocus
            />
            {rejectError && <div className="error-msg">{rejectError}</div>}
          </div>
          <div className="actions">
            <ButtonStyled variant="secondary" onClick={() => modalRejectRef.current?.close()}>
              <span>Cancelar</span>
            </ButtonStyled>
            <DestructiveBtn variant="primary" onClick={handleConfirmReject}>
              <span>Confirmar Rechazo</span>
            </DestructiveBtn>
          </div>
        </RejectModalContent>
      </Modal>
    </>
  )
}

export default ProductItemRow
